"use client";

import * as X from "@/ui";
import { Suspense } from "react";
import { useRoute } from "./common";

import { useSearchParams } from "next/navigation";
import { useIntercom } from "@/providers/intercom";

import { MenuMobile } from "./menu-mobile";
import { MenuDesktop } from "./menu-desktop";
import { FooterMobile } from "./footer-mobile";
import { FooterDesktop } from "./footer-desktop";

import WeChatQr from "@/ui/shared/assets/qr-wechat.svg";


export function MainLayout(props: X.PageProps<{
  children?: React.ReactNode;
  altHrefs?: { [k in X.Locale]: X.PageHref };
  withHeroImage?: boolean;
  noTopPadding?: boolean;
}>) {
  const isScrolled = useScrolledState({ passiveMode: true });
  const [mobileMenuOpen, setMenuMobileOpen] = X.useState(false);
  const [wechatQrOpen, setWechatQrOpen] = X.useState(false);

  const withTopPadding = !props.noTopPadding && !props.withHeroImage;

  X.useBodyScrollLock(mobileMenuOpen);

  const route = useRoute();
  const showIntercom = route[0] !== "retail";
  useIntercom(showIntercom);

  return (
    <>
      <div className={X.clsx(
        "flex min-h-svh flex-col",
      )}>
        <MenuMobile
          locale={props.locale}
          isOpen={mobileMenuOpen}
          setIsOpen={setMenuMobileOpen}
          altHrefs={props.altHrefs}
          withHeroImage={props.withHeroImage}
          isScrolled={isScrolled}
        />

        <MenuDesktop
          locale={props.locale}
          altHrefs={props.altHrefs}
          withHeroImage={props.withHeroImage}
          isScrolled={isScrolled}
        />

        <main className={X.clsx(
          "flex-grow transition-opacity duration-300",
          mobileMenuOpen && "opacity-0" || "opacity-100",
          withTopPadding && "pt-10 xl:pt-20",
        )}>
          {props.children}
        </main>

        <div id="footer">
          <FooterDesktop
            locale={props.locale}
            altHrefs={props.altHrefs}
            onShowWechatQr={() => setWechatQrOpen(true)}
          />

          <FooterMobile
            locale={props.locale}
            altHrefs={props.altHrefs}
            className={X.clsx(
              "transition-opacity duration-300",
              mobileMenuOpen && "opacity-0" || "opacity-100",
            )}
          />
        </div>

        <Suspense>
          <WithAutoScroll />
        </Suspense>
      </div>

      <WeChatModal
        isOpen={wechatQrOpen}
        onClose={() => setWechatQrOpen(false)}
      />
    </>
  );
}


function WeChatModal(props: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const tr = X.useTranslator();

  return (
    <X.Modal onClose={props.onClose}>
      {props.isOpen && (
        <div className="cursor-pointer" onClick={() => props.onClose()}>
          <X.Image
            src={WeChatQr}
            alt="WeChat"
            className="rounded-2xl max-w-[80vw]"
          />
          <div className="bg-black rounded-b-2xl text-white flex flex-col items-center py-4 px-4 gap-2">
            <p>{tr({ en: "Click anywhere to close", fr: "Cliquez n'importe où pour fermer" })}</p>
            <p className="text-yellow">单击任意位置即可关闭</p>
          </div>
        </div>
      )}
    </X.Modal>
  );
}


function useScrolledState(config?: {
  minScroll?: number;
  passiveMode?: boolean;
}) {
  const minScroll = config?.minScroll ?? 1;
  const [isScrolled, setIsScrolled] = X.useState(false);

  X.useEffect([isScrolled, minScroll], () => {
    const handleScroll = () => {
      if (document.body.style.position === "fixed") {
        // ignore when a modal is open
        return;
      }

      const shouldBeScrolled = window.scrollY > minScroll;

      if (shouldBeScrolled !== isScrolled) {
        setIsScrolled(shouldBeScrolled);
      }
    };

    // passive listener -> better performance
    window.addEventListener("scroll", handleScroll, { passive: config?.passiveMode ?? false });

    // apply directly
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  });

  return isScrolled;
}


function WithAutoScroll() {
  useAutoScroll();

  return (<></>);
}


/**
 * Scroll automatically to the desired hash on page change/load
 */
export function useAutoScroll() {
  const pathname = X.usePathname();
  const searchParams = useSearchParams();

  X.useEffect([pathname, searchParams], () => {
    const hash = window.location.hash.slice(1);

    if (!hash) {
      return;
    }

    // try immediately (for static content)
    scrollTo(hash);

    // fallback for dynamically loaded content
    const observer = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) {
        scrollTo(hash);
        observer.disconnect();
      }
    }, {
      threshold: 0.1,
    });

    document.querySelectorAll("[id]").forEach(
      el => observer.observe(el)
    );

    // cleanup if not found
    const timeout = setTimeout(() => observer.disconnect(), 3000);

    return () => {
      observer.disconnect()
      clearTimeout(timeout)
    };

  });
}


function scrollTo(elementId: string) {
  const element = document.getElementById(elementId);

  if (element) {
    element.scrollIntoView({
      behavior: "smooth",
    });

    // remove the hash
    window.history.replaceState(null, "", window.location.pathname);
  }
}
